import { useCallback, useEffect, useRef, useState } from "react";
import { Phone, MessageCircle, Users, GraduationCap, X, AlertTriangle, ArrowRight } from "lucide-react";
import {
    CONTACTS, PARENT_ALTERNATIVES_BY_SERVICE,
    resolveContactFromHref, telHref, waHref, greetingFor,
} from "../lib/contactConfig";

/**
 * V171 — Smart Contact Router
 * ────────────────────────────────────────────────────────────────
 * Global click interceptor: catches every `<a href="tel:…">` or
 * `<a href="https://wa.me/…">` whose number is one of the 6 configured
 * Anand Tutorials lines, and asks the visitor "Parent/Student or Tutor?"
 * before completing the action.
 *
 *   • Parents: see which service they're reaching + an alternative panel
 *     for other services (e.g. clicked Jabalpur, needs Online 1:1).
 *   • Tutors : WhatsApp ONLY to +91 7610702222. No Call button ever,
 *     even if they clicked a Call link to begin with.
 *
 * Short-circuit: a direct click on the tutor-only line (+91 7610702222)
 * opens WhatsApp straight away — we already know who they are.
 *
 * The interceptor is passive on anchors whose href doesn't match our
 * 6 numbers, so unrelated tel: / wa.me: links (e.g. a tutor's personal
 * phone inside the admin dashboard) remain untouched.
 */

const DEFAULT_TEXT = "Hi Anand Tutorials, I would like to know more. Please help.";
// V173 — Legacy default used only as a last-resort fallback. Every real
// contact in the modal now derives its WhatsApp greeting from greetingFor()
// so the receiving team can tell Parent vs Tutor and city/service at a glance.

function openExternal(href) {
    // tel: links use location; wa.me opens a new tab so the current tab is preserved.
    if (/^tel:/i.test(href)) {
        window.location.href = href;
    } else {
        const w = window.open(href, "_blank", "noopener,noreferrer");
        if (!w) window.location.href = href;
    }
}

// --------------------------------------------------------------- Modal

function RoutingModal({ contact, onClose }) {
    // Flow: pick user type → show the right panel
    const [userType, setUserType] = useState(null); // "parent" | "tutor" | null

    const isMakeover = contact.service === "makeover";
    const altKeys = PARENT_ALTERNATIVES_BY_SERVICE[contact.service] || [];
    const alts = altKeys.map((k) => CONTACTS[k]).filter(Boolean);

    // V173 — Context-aware WhatsApp pre-filled text. Each contact (primary +
    // every alternative) gets its OWN greeting tailored to its service/city
    // and the identified user type. parentText is used for the top-of-modal
    // CTAs after the visitor identifies as a Parent/Student.
    const parentText = greetingFor(contact, "parent");

    // ESC closes
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);

    return (
        <div
            className="fixed inset-0 bg-black/60 z-[200] flex items-end sm:items-center justify-center overflow-y-auto p-0 sm:p-4"
            style={{ width: "100vw", height: "100vh" }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="smart-contact-title"
            data-testid="smart-contact-router"
            data-smart-contact-skip="true"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                className="bg-white w-full sm:w-auto sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl min-h-[40vh] sm:min-h-0 max-h-[92vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sticky top-0 bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center justify-between z-10">
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
                            Anand Tutorials · Contact
                        </div>
                        <div id="smart-contact-title" className="text-base sm:text-lg font-heading font-bold text-[#0F172A] truncate">
                            {userType === null && "How can we help you?"}
                            {userType === "parent" && contact.label}
                            {userType === "tutor" && "Tutor Communication"}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="smart-contact-close"
                        aria-label="Close"
                        className="shrink-0 p-2 hover:bg-slate-100 rounded-lg text-slate-600"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-4 sm:p-6 space-y-4">
                    {userType === null && (
                        <>
                            <p className="text-sm text-slate-600">
                                Please select who you are so we can route you to the right team.
                            </p>
                            <button
                                type="button"
                                onClick={() => setUserType("parent")}
                                data-testid="smart-contact-pick-parent"
                                className="w-full flex items-start gap-3 p-4 rounded-xl border-2 border-slate-200 hover:border-cyan-400 hover:bg-cyan-50/40 transition-colors text-left"
                            >
                                <div className="w-10 h-10 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center shrink-0">
                                    <Users className="w-5 h-5" />
                                </div>
                                <div className="min-w-0">
                                    <div className="font-heading font-bold text-[#0F172A] text-base">
                                        I'm a Parent / Student
                                    </div>
                                    <div className="text-xs text-slate-600 mt-0.5">
                                        I'm looking for Home Tuition or 1-on-1 Online Classes.
                                    </div>
                                </div>
                            </button>
                            <button
                                type="button"
                                onClick={() => setUserType("tutor")}
                                data-testid="smart-contact-pick-tutor"
                                className="w-full flex items-start gap-3 p-4 rounded-xl border-2 border-slate-200 hover:border-fuchsia-400 hover:bg-fuchsia-50/40 transition-colors text-left"
                            >
                                <div className="w-10 h-10 rounded-full bg-fuchsia-100 text-fuchsia-700 flex items-center justify-center shrink-0">
                                    <GraduationCap className="w-5 h-5" />
                                </div>
                                <div className="min-w-0">
                                    <div className="font-heading font-bold text-[#0F172A] text-base">
                                        I'm a Tutor
                                    </div>
                                    <div className="text-xs text-slate-600 mt-0.5">
                                        I want to contact Anand Tutorials regarding tutor-related matters.
                                    </div>
                                </div>
                            </button>
                        </>
                    )}

                    {userType === "parent" && (
                        <>
                            <div className="rounded-xl bg-cyan-50 border border-cyan-200 p-3 text-sm text-cyan-900">
                                <div className="font-semibold">
                                    {contact.service === "home_tuition" && `${contact.city} Home Tuition & Tutoring`}
                                    {contact.service === "online_1_1" && "1-on-1 Live Online Classes"}
                                    {contact.service === "makeover" && "1-on-1 Makeover Classes"}
                                </div>
                                <p className="text-xs mt-1 text-cyan-800 leading-snug">
                                    {contact.service === "home_tuition" && `You're in the right place if you're looking for ${contact.city} Home Tuition or 1-on-1 tutoring for a student in ${contact.city}.`}
                                    {contact.service === "online_1_1" && "You're in the right place for Pan India and Worldwide 1-on-1 Online Classes — including NRI / International students."}
                                    {contact.service === "makeover" && "This line handles Anand Tutorials' dedicated 1-on-1 Makeover Classes."}
                                </p>
                            </div>

                            <div className="grid grid-cols-2 gap-2">
                                <a
                                    href={telHref(contact)}
                                    onClick={() => setTimeout(onClose, 50)}
                                    data-testid="smart-contact-call"
                                    className="flex items-center justify-center gap-2 px-3 py-3 rounded-xl bg-[#0F172A] text-white font-semibold text-sm hover:bg-[#1E293B]"
                                >
                                    <Phone className="w-4 h-4" /> Call {contact.teamLabel}
                                </a>
                                <a
                                    href={waHref(contact, parentText)}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={() => setTimeout(onClose, 50)}
                                    data-testid="smart-contact-whatsapp"
                                    data-smart-contact-skip="true"
                                    className="flex items-center justify-center gap-2 px-3 py-3 rounded-xl bg-[#25D366] text-white font-semibold text-sm hover:bg-[#1ebe5a]"
                                >
                                    <MessageCircle className="w-4 h-4" /> WhatsApp {contact.teamLabel}
                                </a>
                            </div>

                            {alts.length > 0 && (
                                <div className="pt-3 border-t border-slate-200">
                                    <div className="text-[11px] uppercase tracking-widest text-slate-400 font-bold mb-2">
                                        {isMakeover
                                            ? "Looking for a different service?"
                                            : contact.service === "home_tuition"
                                                ? "Looking for 1-on-1 Online Classes instead?"
                                                : "Need a city-based Home Tuition service?"}
                                    </div>
                                    <div className="space-y-1.5">
                                        {alts.map((a) => (
                                            <div
                                                key={a.key}
                                                data-testid={`smart-contact-alt-${a.key}`}
                                                className="flex items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2"
                                            >
                                                <div className="min-w-0">
                                                    <div className="font-semibold text-sm text-[#0F172A] truncate">{a.label}</div>
                                                    <div className="text-[11px] text-slate-500">Dedicated {a.teamLabel.toLowerCase()}</div>
                                                </div>
                                                <div className="shrink-0 flex items-center gap-1">
                                                    <a
                                                        href={telHref(a)}
                                                        onClick={() => setTimeout(onClose, 50)}
                                                        data-testid={`smart-contact-alt-call-${a.key}`}
                                                        aria-label={`Call ${a.teamLabel}`}
                                                        title={`Call ${a.teamLabel}`}
                                                        className="p-2 rounded-lg bg-white border border-slate-200 text-slate-700 hover:border-cyan-400 hover:text-cyan-700"
                                                    >
                                                        <Phone className="w-4 h-4" />
                                                    </a>
                                                    <a
                                                        href={waHref(a, greetingFor(a, "parent", { explicit: true }))}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        onClick={() => setTimeout(onClose, 50)}
                                                        data-testid={`smart-contact-alt-wa-${a.key}`}
                                                        data-smart-contact-skip="true"
                                                        aria-label={`WhatsApp ${a.teamLabel}`}
                                                        title={`WhatsApp ${a.teamLabel}`}
                                                        className="p-2 rounded-lg bg-[#25D366]/10 border border-[#25D366]/30 text-[#128C7E] hover:bg-[#25D366]/20"
                                                    >
                                                        <MessageCircle className="w-4 h-4" />
                                                    </a>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {userType === "tutor" && (
                        <>
                            <div className="rounded-xl bg-fuchsia-50 border border-fuchsia-200 p-3 text-sm text-fuchsia-900">
                                <div className="flex items-start gap-2">
                                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                    <div>
                                        <div className="font-semibold">Tutor Communication — WhatsApp Only</div>
                                        <p className="text-xs mt-1 leading-snug">
                                            If you are a tutor, please <strong>do not call</strong> our contact numbers.
                                            For tutor-related communication, please send us a WhatsApp message only.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <a
                                href={waHref(CONTACTS.TUTOR_WHATSAPP, greetingFor(CONTACTS.TUTOR_WHATSAPP, "tutor"))}
                                target="_blank"
                                rel="noreferrer"
                                onClick={() => setTimeout(onClose, 50)}
                                data-testid="smart-contact-tutor-whatsapp"
                                data-smart-contact-skip="true"
                                className="w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl bg-[#25D366] text-white font-semibold text-sm hover:bg-[#1ebe5a]"
                            >
                                <MessageCircle className="w-5 h-5" />
                                WhatsApp Tutor Team
                            </a>

                            <p className="text-[11px] text-slate-500 text-center">
                                This is the only channel used for tutor communication at Anand Tutorials.
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

// --------------------------------------------------------------- Router

export default function SmartContactRouter() {
    const [routing, setRouting] = useState(null);  // active contact | null
    const lastClickRef = useRef(0);  // debounce double-click

    const handleClick = useCallback((e) => {
        // Opt-out: any element can set `data-smart-contact-skip="true"` to bypass.
        const anchor = e.target.closest && e.target.closest("a[href]");
        if (!anchor) return;
        if (anchor.dataset?.smartContactSkip === "true") return;
        if (anchor.closest("[data-smart-contact-skip='true']")) return;

        const href = anchor.getAttribute("href") || "";
        const contact = resolveContactFromHref(href);
        if (!contact) return;  // Not one of our 6 lines — let it through untouched.

        // Debounce accidental double-fires (iOS Safari fires both touch + click).
        const now = Date.now();
        if (now - lastClickRef.current < 400) {
            e.preventDefault();
            return;
        }
        lastClickRef.current = now;

        // Short-circuit: tutor-only line is unambiguous — skip "who are you?".
        // But still enforce WhatsApp-only (if someone built a tel: link against
        // this number, convert it to a wa.me link silently).
        if (contact.key === "tutor_whatsapp") {
            e.preventDefault();
            openExternal(waHref(contact, greetingFor(contact, "tutor")));
            return;
        }

        // Everyone else: ask who they are first.
        e.preventDefault();
        setRouting(contact);
    }, []);

    useEffect(() => {
        // Capture phase so we beat any inline onClick handlers that would
        // otherwise navigate away before we see the click.
        document.addEventListener("click", handleClick, true);
        return () => document.removeEventListener("click", handleClick, true);
    }, [handleClick]);

    if (!routing) return null;
    return <RoutingModal contact={routing} onClose={() => setRouting(null)} />;
}
